import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    Typography,
} from '@mui/material';
import { useHubStyles } from '../../hooks/useHubStyles';
import { HUB_TYPES } from '../../utils/hubRoutes';
import {
    getHubDialogActionsSx,
    getHubDialogCancelButtonSx,
    getHubDialogContentSx,
    getHubDialogPaperSx,
    getHubDialogSubmitButtonSx,
} from '../../theme/hubThemes';
import { HubDialogCancelButton, HubDialogSubmitButton } from './HubDialogButtons';

export { HubDialogCancelButton, HubDialogSubmitButton };

export default function HubDialog({
    open,
    onClose,
    title,
    subtitle,
    description,
    icon: Icon,
    headerContent,
    hubType = HUB_TYPES.PROJECT_MANAGEMENT,
    maxWidth = 'sm',
    fullWidth = true,
    disableClose = false,
    paperSx,
    contentSx,
    contentDividers = false,
    children,
    actions,
    cancelLabel,
    onCancel,
    submitLabel,
    onSubmit,
    submitting = false,
    submitDisabled = false,
    submitStartIcon,
    submitColor,
}) {
    const styles = useHubStyles(hubType);
    const handleClose = disableClose ? undefined : onClose;
    const resolvedCancel = onCancel || onClose;
    const showDefaultActions = !actions && (cancelLabel || submitLabel || onSubmit);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            PaperProps={{
                sx: getHubDialogPaperSx(styles, paperSx),
            }}
        >
            <Box
                sx={{
                    bgcolor: styles.headerBg,
                    color: styles.headerText,
                    px: 2.5,
                    py: 2,
                }}
            >
                {headerContent || (
                    <>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            {Icon && <Icon sx={{ fontSize: 26, color: styles.headerText }} />}
                            <Typography variant="h6" fontWeight={700} sx={{ color: styles.headerText }}>
                                {title}
                            </Typography>
                        </Stack>
                        {subtitle && (
                            <Typography
                                variant="caption"
                                sx={{ color: styles.headerMuted, mt: 0.75, display: 'block' }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </>
                )}
            </Box>

            <DialogContent
                dividers={contentDividers}
                sx={{
                    ...getHubDialogContentSx(styles),
                    ...contentSx,
                }}
            >
                {description && (
                    <Typography
                        variant="body2"
                        sx={{ color: styles.bodyMuted, lineHeight: 1.55, mb: 2 }}
                    >
                        {description}
                    </Typography>
                )}
                {children}
            </DialogContent>

            {(actions || showDefaultActions) && (
                <DialogActions sx={getHubDialogActionsSx(styles)}>
                    {actions || (
                        <>
                            {cancelLabel && (
                                <Button
                                    onClick={resolvedCancel}
                                    disabled={submitting}
                                    sx={getHubDialogCancelButtonSx(styles)}
                                >
                                    {cancelLabel}
                                </Button>
                            )}
                            {(submitLabel || onSubmit) && (
                                <Button
                                    onClick={onSubmit}
                                    variant="contained"
                                    disableElevation
                                    color={submitColor || 'primary'}
                                    disabled={submitting || submitDisabled}
                                    startIcon={
                                        submitting
                                            ? <CircularProgress size={16} color="inherit" />
                                            : submitStartIcon
                                    }
                                    sx={submitColor ? undefined : getHubDialogSubmitButtonSx(styles)}
                                >
                                    {submitLabel}
                                </Button>
                            )}
                        </>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
}

export function HubDialogActions({ children, hubType = HUB_TYPES.PROJECT_MANAGEMENT, sx }) {
    const styles = useHubStyles(hubType);
    return (
        <DialogActions sx={{ ...getHubDialogActionsSx(styles), ...sx }}>
            {children}
        </DialogActions>
    );
}
